import React, { useEffect, useMemo, useState } from "react";
import { conciergeApi } from "../services/externalPlatformApi";

const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(value || 0));

const STATUSES = ["NEW", "QUOTED", "BOOKED", "COMPLETED", "DECLINED"];

export default function AdminConcierge() {
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [finance, setFinance] = useState({
    total_booking: 0,
    awin_commission: 0,
    concierge_fee: 0,
  });

  const totals = useMemo(() => {
    return requests.reduce(
      (acc, request) => {
        acc.booking += Number(request.total_booking || 0);
        acc.awin += Number(request.awin_commission || 0);
        acc.fee += Number(request.concierge_fee || 0);
        acc.earned += Number(request.total_hlt_earned || 0);
        return acc;
      },
      { booking: 0, awin: 0, fee: 0, earned: 0 }
    );
  }, [requests]);

  useEffect(() => {
    loadRequests();
  }, []);

  async function loadRequests() {
    setLoading(true);
    setError("");
    try {
      const payload = await conciergeApi.listRequests({ limit: 500 });
      setRequests(Array.isArray(payload?.requests) ? payload.requests : []);
    } catch {
      setError("Could not load concierge requests. The backend endpoint may not be live yet.");
    } finally {
      setLoading(false);
    }
  }

  async function openRequest(id) {
    setError("");
    try {
      const payload = await conciergeApi.getRequest(id);
      setSelected(payload.request);
      setNotes(Array.isArray(payload.notes) ? payload.notes : []);
      setFinance({
        total_booking: payload.request?.total_booking || 0,
        awin_commission: payload.request?.awin_commission || 0,
        concierge_fee: payload.request?.concierge_fee || 0,
      });
    } catch {
      setError("Could not load the concierge request.");
    }
  }

  async function updateRequest(payload) {
    if (!selected) return;
    try {
      const result = await conciergeApi.updateRequest(selected.id, payload);
      setSelected(result.request);
      await loadRequests();
    } catch {
      setError("Could not update the concierge request.");
    }
  }

  async function saveFinance(e) {
    e.preventDefault();
    await updateRequest({
      total_booking: Number(finance.total_booking || 0),
      awin_commission: Number(finance.awin_commission || 0),
      concierge_fee: Number(finance.concierge_fee || 0),
    });
  }

  async function addNote(e) {
    e.preventDefault();
    if (!selected || !note.trim()) return;
    try {
      await conciergeApi.addNote(selected.id, { note });
      setNote("");
      await openRequest(selected.id);
    } catch {
      setError("Could not add the note.");
    }
  }

  return (
    <div className="page space-y-6">
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Concierge CRM</h1>
            <p className="mt-1 text-slate-300">
              Review package requests, track quotes and log HLT earnings.
            </p>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-4 md:mt-0">
            <KPI label="Requests" value={requests.length} />
            <KPI label="Booking value" value={money(totals.booking)} />
            <KPI label="Awin" value={money(totals.awin)} />
            <KPI label="HLT earned" value={money(totals.earned)} />
          </div>
        </div>
        {error && (
          <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
            {error}
          </div>
        )}
      </section>

      <section className="grid gap-6 lg:grid-cols-[360px,1fr]">
        <div className="space-y-3">
          {loading && <div className="card text-slate-300">Loading concierge inbox...</div>}
          {!loading && requests.length === 0 && (
            <div className="card text-slate-300">No concierge requests yet.</div>
          )}
          {!loading &&
            requests.map((request) => (
              <button
                key={request.id}
                type="button"
                className={`block w-full rounded-2xl border p-4 text-left transition ${
                  selected?.id === request.id
                    ? "border-brand-600/60 bg-brand-600/10"
                    : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                }`}
                onClick={() => openRequest(request.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="font-semibold text-white">{request.name}</h2>
                    <p className="text-sm text-slate-400">{request.destination || "Destination TBC"}</p>
                  </div>
                  <span className="badge">{request.status}</span>
                </div>
                <p className="mt-2 text-sm text-slate-300">{request.budget || "Budget TBC"}</p>
              </button>
            ))}
        </div>

        {!selected ? (
          <div className="card text-slate-300">Select a request to view details.</div>
        ) : (
          <article className="card space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold text-white">{selected.name}</h2>
                <p className="mt-1 text-slate-300">
                  {selected.email} {selected.phone ? `- ${selected.phone}` : ""}
                </p>
              </div>
              <select
                className="input w-44"
                value={selected.status}
                onChange={(e) => updateRequest({ status: e.target.value })}
              >
                {STATUSES.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Metric label="Destination" value={selected.destination || "TBC"} />
              <Metric label="Party size" value={selected.party_size || "TBC"} />
              <Metric label="Dates" value={selected.dates || "TBC"} />
              <Metric label="Client type" value={selected.client_type || "budget"} />
              <Metric label="Budget" value={selected.budget || "TBC"} />
              <Metric
                label="Services"
                value={Array.isArray(selected.services) ? selected.services.join(", ") : selected.services || "TBC"}
              />
            </div>

            {selected.special_requests && (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">
                  Special requests
                </div>
                <p className="mt-2 text-slate-200">{selected.special_requests}</p>
              </div>
            )}

            <form className="rounded-xl border border-white/10 bg-white/[0.03] p-4" onSubmit={saveFinance}>
              <h3 className="text-lg font-semibold text-white">Financial tracking</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Field label="Booking value">
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={finance.total_booking}
                    onChange={(e) => setFinance((current) => ({ ...current, total_booking: e.target.value }))}
                  />
                </Field>
                <Field label="Awin commission">
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={finance.awin_commission}
                    onChange={(e) => setFinance((current) => ({ ...current, awin_commission: e.target.value }))}
                  />
                </Field>
                <Field label="Concierge fee">
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={finance.concierge_fee}
                    onChange={(e) => setFinance((current) => ({ ...current, concierge_fee: e.target.value }))}
                  />
                </Field>
              </div>
              <button className="btn-primary mt-3">Save finance</button>
            </form>

            <form onSubmit={addNote} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <h3 className="text-lg font-semibold text-white">Notes</h3>
              <textarea
                className="textarea mt-3 min-h-24"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add an internal note..."
              />
              <button className="btn-primary mt-3">Add note</button>
              <div className="mt-4 space-y-2">
                {notes.length === 0 ? (
                  <p className="text-sm text-slate-400">No notes yet.</p>
                ) : (
                  notes.map((item) => (
                    <div key={item.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                      <div className="text-xs uppercase tracking-wider text-slate-500">
                        {item.note_type} - {item.author}
                      </div>
                      <p className="mt-1 text-sm text-slate-200">{item.note}</p>
                    </div>
                  ))
                )}
              </div>
            </form>
          </article>
        )}
      </section>
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-semibold text-white">{value}</div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-white">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}
